import React from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { CalendarDays, Sun, Moon, FolderKanban, Users, Truck, LayoutDashboard, LogOut, Package, Clock, CreditCard, Settings } from "lucide-react";

function NavItem({ to, icon: Icon, label, testId }) {
  return (
    <NavLink
      to={to}
      data-testid={testId}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 border-2 border-transparent rounded-md font-bold text-sm uppercase tracking-wider ${
          isActive ? "bg-[#EA580C] text-white border-slate-900" : "text-slate-100 hover:bg-slate-800"
        }`
      }
    >
      <Icon size={20} strokeWidth={2.5} />
      <span>{label}</span>
    </NavLink>
  );
}

export default function Layout() {
  const { user, company, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();

  const office = user && ["OWNER", "PLANNER"].includes(user.role);

  const onLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--pb-bg)" }}>
      {/* Sidebar (desktop) */}
      <aside className="hidden md:flex md:flex-col w-64 bg-slate-900 border-r-2 border-slate-900 sticky top-0 h-screen">
        <div className="p-5 border-b-2 border-slate-800">
          <div className="text-white">
            <img src="/logo-ploegbord.webp?v=8" alt="Ploegbord" className="h-10 w-auto" />
            {company?.name && (
              <div className="text-xs uppercase tracking-widest text-slate-400 mt-2 font-bold" data-testid="sidebar-company-name">{company.name}</div>
            )}
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {office && <NavItem to="/planbord" icon={CalendarDays} label="Planbord" testId="nav-planbord" />}
          {office && <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" testId="nav-dashboard" />}
          <NavItem to="/vandaag" icon={Sun} label="Vandaag" testId="nav-vandaag" />
          <NavItem to="/projecten" icon={FolderKanban} label="Projecten" testId="nav-projecten" />
          {office && <NavItem to="/medewerkers" icon={Users} label="Medewerkers" testId="nav-medewerkers" />}
          {office && <NavItem to="/uren" icon={Clock} label="Uren" testId="nav-uren" />}
          {office && <NavItem to="/voertuigen" icon={Truck} label="Voertuigen" testId="nav-voertuigen" />}
          {office && <NavItem to="/materiaal" icon={Package} label="Materiaal" testId="nav-materiaal" />}
          {office && <NavItem to="/instellingen" icon={Settings} label="Instellingen" testId="nav-instellingen" />}
          {user?.role === "OWNER" && <NavItem to="/billing" icon={CreditCard} label="Facturatie" testId="nav-billing" />}
        </nav>
        <div className="p-3 border-t-2 border-slate-800 space-y-2">
          <div className="px-3 py-2 text-white">
            <div className="text-sm font-bold" data-testid="current-user-name">{user?.name}</div>
            <div className="text-xs uppercase tracking-widest text-orange-400">{user?.role}</div>
          </div>
          <button
            onClick={toggle}
            data-testid="btn-theme-toggle"
            className="w-full flex items-center gap-2 justify-center rounded-md font-bold py-3 border-2 border-slate-700 text-white hover:bg-slate-800"
            aria-label="Wissel donker/licht"
          >
            {theme === "dark" ? <Sun size={18}/> : <Moon size={18}/>}
            {theme === "dark" ? "Lichte modus" : "Donkere modus"}
          </button>
          <button
            onClick={onLogout}
            data-testid="btn-logout"
            className="w-full flex items-center gap-2 pb-btn-outline !border-slate-100 !text-white !bg-slate-800 hover:!bg-slate-700 justify-center"
          >
            <LogOut size={18} /> Uitloggen
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-slate-900 text-white border-b-2 border-slate-900 flex items-center justify-between px-4 h-14">
        <div className="flex items-center gap-2 min-w-0">
          <img src="/logo-ploegbord.webp?v=8" alt="Ploegbord" className="h-8 w-auto" />
          {company?.name && (
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold truncate" data-testid="mobile-company-name">{company.name}</div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button onClick={toggle} data-testid="btn-theme-toggle-mobile" className="text-white p-1" aria-label="Wissel donker/licht">
            {theme === "dark" ? <Sun size={18}/> : <Moon size={18}/>}
          </button>
          <button onClick={onLogout} data-testid="btn-logout-mobile" className="text-sm font-bold uppercase">Uitloggen</button>
        </div>
      </div>

      <main className="flex-1 min-w-0 pt-14 md:pt-0 pb-20 md:pb-0">
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav className={`md:hidden fixed bottom-0 inset-x-0 z-40 bg-slate-900 border-t-2 border-slate-900 grid ${office ? "grid-cols-4" : "grid-cols-2"}`}>
        <NavLink to="/vandaag" className={({isActive}) => `flex flex-col items-center py-2 text-xs font-bold ${isActive?"text-orange-400":"text-white"}`} data-testid="mnav-vandaag">
          <Sun size={22} /> VANDAAG
        </NavLink>
        <NavLink to="/projecten" className={({isActive}) => `flex flex-col items-center py-2 text-xs font-bold ${isActive?"text-orange-400":"text-white"}`} data-testid="mnav-projecten">
          <FolderKanban size={22} /> PROJECTEN
        </NavLink>
        {office && (
          <NavLink to="/planbord" className={({isActive}) => `flex flex-col items-center py-2 text-xs font-bold ${isActive?"text-orange-400":"text-white"}`} data-testid="mnav-planbord">
            <CalendarDays size={22} /> PLANBORD
          </NavLink>
        )}
        {office && (
          <NavLink to="/dashboard" className={({isActive}) => `flex flex-col items-center py-2 text-xs font-bold ${isActive?"text-orange-400":"text-white"}`} data-testid="mnav-dashboard">
            <LayoutDashboard size={22} /> DASH
          </NavLink>
        )}
      </nav>
    </div>
  );
}
